import React, { useEffect, useRef, useState } from "react";

import EventSystem from "@/service/EventSystem";
import { useStateRef } from "@/utils/hooks";

import Slide, { SlideProps } from "./Slide";
import {
  SliderBackground,
  SliderContainer,
  SliderWrapper,
  SlidesWrapper,
} from "./styles";

export enum SliderActions {
  next = "next",
  prev = "prev",
  goto = "goto",
}

export type SliderProps = {
  slides: SlideProps[];
  timing: number;
  selected: number;
  backgroundImageUrl?: string;
  backgroundColor?: string;
  isSlideVoted?: (slideId: string) => boolean;
  onSlideWillChange?: (slideIndex: number) => void;
  onSlideChanged?: (slideIndex: number) => void;
  onEnd?: () => void;
};

type Position = {
  x: number;
  y: number;
};

const Slider: React.FC<SliderProps> = (props: SliderProps): JSX.Element => {
  const {
    slides,
    timing,
    backgroundImageUrl,
    backgroundColor,
    isSlideVoted,
    onSlideWillChange,
    onSlideChanged,
    onEnd,
  } = props;

  const findBackgroundImage = (slideIndex: number): string | undefined =>
    slides[slideIndex]?.backgroundImageUrl || backgroundImageUrl;

  const [active, setActive, activeRef] = useStateRef<number>(props.selected);
  const [transition, setTransition] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>();
  const [backgroundImage, setBackgroundImage] = useState<string | undefined>(
    findBackgroundImage(props.selected)
  );
  const [prevBackgroundImage, setPrevBackgroundImage] = useState<
    string | undefined
  >();

  const dragStartRef = useRef<Position>({ x: 0, y: 0 });

  useEffect(() => {
    EventSystem.subscribe("slider:action:prev", prev);
    EventSystem.subscribe("slider:action:next", next);
    EventSystem.subscribe("slider:action:goto", goto);
    EventSystem.subscribe("slider:disabled", setDisabled);

    if (props.onSlideChanged) {
      // Hint: This is for the case that someone returns to the interaction
      props.onSlideChanged(props.selected);
    }
  }, []);

  const next = () => {
    if (activeRef.current < slides.length - 1) {
      goto(activeRef.current + 1);
    } else if (onEnd) {
      onEnd();
    }
  };

  const prev = () => {
    if (activeRef.current > 0) {
      goto(activeRef.current - 1);
    }
  };

  const goto = (slideIndex: number) => {
    if (transition) {
      return;
    }
    setTransition(true);
    setActive(slideIndex);
    setBackgroundImage(findBackgroundImage(slideIndex));
    setPrevBackgroundImage(backgroundImage);

    if (onSlideWillChange) {
      onSlideWillChange(slideIndex);
    }
    setTimeout(() => {
      if (onSlideChanged) {
        onSlideChanged(slideIndex);
      }
      setTransition(false);
    }, timing);
  };

  const getDragPosition = (event: React.TouchEvent): Position => {
    return {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY,
    };
  };

  const handleDragStart = (event: React.TouchEvent) => {
    dragStartRef.current = getDragPosition(event);
  };

  const getSliderAction = (
    dragStart: Position,
    dragEnd: Position
  ): SliderActions.next | SliderActions.prev | undefined => {
    const minXDelta = 50;
    const maxAngleDeg = 25;

    const dragOffsetX = dragEnd.x - dragStart.x;
    const dragOffsetY = dragEnd.y - dragStart.y;
    const deltaY = Math.abs(dragOffsetY);
    const deltaX = Math.abs(dragOffsetX);

    const angleDeg = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;
    if (deltaX < minXDelta || angleDeg > maxAngleDeg) {
      return;
    }
    return dragOffsetX < 0 ? SliderActions.next : SliderActions.prev;
  };

  const handleDragEnd = (event: React.TouchEvent): void => {
    const dragEnd = getDragPosition(event);
    const action = getSliderAction(dragStartRef.current, dragEnd);
    if (!action) {
      return;
    }

    // Hint: This is now interpreted as a swipe, prevent click event (is this needed for some browsers?).
    event.preventDefault();
    event.stopPropagation();

    if (action === SliderActions.next) {
      if (
        !slides[active].key.match(/intro|summary/) &&
        isSlideVoted &&
        !isSlideVoted(slides[active].key)
      ) {
        // Hint: intro|summary are values from outside. This should be handled outside of the component.
        return;
      }
      next();
    } else {
      prev();
    }
  };

  const renderBackground = (): JSX.Element => {
    // Hint: Two overlapping images, the second fades over the first
    return (
      <>
        {backgroundImage && prevBackgroundImage && (
          <SliderBackground backgroundImageUrl={prevBackgroundImage} />
        )}
        {!transition &&
          backgroundImage &&
          prevBackgroundImage !== backgroundImage && (
            <SliderBackground
              backgroundImageUrl={backgroundImage}
              timing={timing}
            />
          )}
      </>
    );
  };

  return (
    <SliderWrapper disabled={disabled || transition}>
      <SliderContainer
        backgroundColor={backgroundColor}
        backgroundImageUrl={backgroundImageUrl}
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
      >
        {renderBackground()}
        <SlidesWrapper
          style={{
            left: `${-1 * active * 100}%`,
          }}
          transition={transition}
          timing={timing}
        >
          {slides.map((slide: SlideProps, index: number) => (
            <Slide
              key={slide.key}
              meta={slide.meta}
              children={slide.children}
              isActive={active === index}
            />
          ))}
        </SlidesWrapper>
      </SliderContainer>
    </SliderWrapper>
  );
};
export default Slider;
